import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDeveloperContext } from '@/hooks/useDeveloperContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2 } from 'lucide-react';

const STRATEGY_LABELS: Record<string, string> = {
  round_robin: 'Round-robin',
  by_project: 'By project (first member)',
  by_load: 'By load (least busy)',
  manual: 'Manual only',
};

export default function DeveloperLeadRules() {
  const { ctx } = useDeveloperContext();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({
    name: '', strategy: 'round_robin', priority: 100, project_id: '', member_ids: [] as string[],
  });

  const { data: rules = [] } = useQuery({
    queryKey: ['dev-rules', ctx.developerId],
    enabled: !!ctx.developerId,
    queryFn: async () => {
      const { data } = await supabase
        .from('developer_lead_rules')
        .select('*, projects(name)')
        .eq('developer_id', ctx.developerId!)
        .order('priority', { ascending: true });
      return data || [];
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['dev-projects-list', ctx.developerId],
    enabled: !!ctx.developerId,
    queryFn: async () => {
      const { data } = await supabase.from('projects').select('id, name').eq('developer_id', ctx.developerId!);
      return data || [];
    },
  });

  const { data: members = [] } = useQuery({
    queryKey: ['dev-team-members-pool', ctx.developerId],
    enabled: !!ctx.developerId,
    queryFn: async () => {
      const { data } = await supabase
        .from('developer_team_members')
        .select('user_id, member_role')
        .eq('developer_id', ctx.developerId!)
        .eq('is_active', true);
      const ids = (data || []).map(d => d.user_id);
      if (!ids.length) return [];
      const { data: profs } = await supabase.from('profiles').select('user_id, full_name').in('user_id', ids);
      const m = new Map((profs || []).map(p => [p.user_id, p.full_name]));
      return (data || []).map(d => ({ user_id: d.user_id, name: m.get(d.user_id) || d.user_id.slice(0, 8), role: d.member_role }));
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const payload = {
        developer_id: ctx.developerId,
        name: form.name,
        strategy: form.strategy,
        priority: Number(form.priority) || 100,
        project_id: form.project_id || null,
        member_ids: form.member_ids,
        is_active: true,
      };
      const { error } = await supabase.from('developer_lead_rules').insert(payload as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Rule created' });
      setOpen(false);
      setForm({ name: '', strategy: 'round_robin', priority: 100, project_id: '', member_ids: [] });
      qc.invalidateQueries({ queryKey: ['dev-rules'] });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Failed', description: e.message }),
  });

  const toggle = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from('developer_lead_rules').update({ is_active }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dev-rules'] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('developer_lead_rules').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dev-rules'] }),
  });

  if (!ctx.isAdmin) return <div className="text-sm text-muted-foreground">Only the developer admin can manage rules.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">Lead Assignment Rules</h2>
          <p className="text-sm text-muted-foreground">Rules run in priority order on every new lead.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New Rule</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create assignment rule</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Skyline Towers — round robin" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Strategy</Label>
                  <Select value={form.strategy} onValueChange={(v) => setForm({ ...form, strategy: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STRATEGY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Priority (lower runs first)</Label><Input type="number" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Project (optional — restricts the rule)</Label>
                <Select value={form.project_id || 'all'} onValueChange={(v) => setForm({ ...form, project_id: v === 'all' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All projects</SelectItem>
                    {projects.map((p: any) => <SelectItem key={p.id} value={p.id}>{typeof p.name === 'object' ? (p.name?.en || p.id) : (p.name || p.id)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Eligible members (leave blank → all sales members)</Label>
                <div className="space-y-1 max-h-40 overflow-y-auto border rounded-md p-2">
                  {members.length === 0 ? <div className="text-xs text-muted-foreground">No team members yet.</div> :
                    members.map((m: any) => (
                      <label key={m.user_id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={form.member_ids.includes(m.user_id)}
                          onCheckedChange={(c) => setForm({
                            ...form,
                            member_ids: c ? [...form.member_ids, m.user_id] : form.member_ids.filter((x: string) => x !== m.user_id),
                          })}
                        />
                        {m.name} <span className="text-xs text-muted-foreground">({m.role})</span>
                      </label>
                    ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => create.mutate()} disabled={!form.name}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {rules.length === 0 && <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No rules yet. New leads will stay unassigned for manual handling.</CardContent></Card>}
        {rules.map((r: any) => (
          <Card key={r.id}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{r.name}</span>
                  <Badge variant="outline">{STRATEGY_LABELS[r.strategy] || r.strategy}</Badge>
                  <Badge variant="secondary">priority {r.priority}</Badge>
                  {r.project_id && <Badge>Project: {typeof r.projects?.name === 'object' ? r.projects?.name?.en : r.projects?.name}</Badge>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {r.member_ids?.length ? `${r.member_ids.length} member(s) in pool` : 'All active sales members'}
                </div>
              </div>
              <Switch checked={r.is_active} onCheckedChange={(v) => toggle.mutate({ id: r.id, is_active: v })} />
              <Button variant="ghost" size="icon" onClick={() => remove.mutate(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
